/* global MathJax */
import katex from 'katex';
import * as React from 'react';
import {
    type StyleProp,
    type TextStyle,
    // @ts-ignore
    unstable_createElement,
} from 'react-native';

import { themedFonts, useCurrentTheme } from '@gitbook/react-uikit';
import { Logger } from '@gitbook/util-debug';
import { useHead } from '@gitbook/util-react-head';

const logger = Logger('math');

/**
 * Render a TeX string.
 */
export default function MathTeXView(props: {
    formula: string;
    inline: boolean;
}): React.ReactElement | null {
    const { formula, inline } = props;
    const [, font] = useCurrentTheme(themedFonts);

    const style = [font.text];

    try {
        // try to render with katex
        const html = katex.renderToString(formula, {
            displayMode: !inline,
        });

        return <TeXViewWithKatex html={html} inline={inline} style={style} />;
    } catch (error) {
        if (error instanceof katex.ParseError) {
            logger.error(`error parsing math "${formula}": ${error.message}`);
            // or fallback to use MathJax
            return <TeXViewWithMathJax formula={formula} inline={inline} style={style} />;
        }
        throw error;
    }
}

/**
 * Render a TeX formula from Katex.
 */
function TeXViewWithKatex(props: {
    html: string;
    inline: boolean;
    style?: StyleProp<TextStyle>;
}): React.ReactElement {
    const { inline, html, style } = props;

    useHead((head) => {
        head.write({
            type: 'link',
            rel: 'stylesheet',
            href: `https://cdn.jsdelivr.net/npm/katex@${katex.version}/dist/katex.min.css`,
        });
    });

    return unstable_createElement(inline ? 'span' : 'div', {
        dangerouslySetInnerHTML: { __html: html },
        style,
    });
}

/**
 * Render a TeX formula with MathJax
 */
function TeXViewWithMathJax(props: {
    formula: string;
    inline: boolean;
    style?: StyleProp<TextStyle>;
}): React.ReactElement {
    const { formula, inline, style } = props;
    const containerRef = React.useRef<HTMLDivElement | HTMLSpanElement>(null);
    const scriptRef = React.useRef<HTMLScriptElement | null>(null);
    const prevInline = React.useRef(inline);

    // Suspend and load MathJax
    useHead((head) => {
        head.write({
            type: 'script',
            src: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.4/MathJax.js?config=TeX-MML-AM_CHTML',
        });
    });

    // Configure MathJax once loaded
    React.useEffect(() => {
        MathJax.Hub.Config({
            tex2jax: {
                inlineMath: [],
            },
            showMathMenu: false,
            showMathMenuMSIE: false,
            styles: {
                '.mjx-chtml span': {
                    color: 'var(--sp-color-text-base) !important',
                    'font-size': 'unset !important',
                },
            },
        });
    }, []);

    // Typeset the formula
    React.useEffect(() => {
        if (prevInline.current !== inline && scriptRef.current) {
            const jax = MathJax.Hub.getJaxFor(scriptRef.current);
            if (jax) {
                jax.Remove();
            }

            scriptRef.current = null;
        }

        if (scriptRef.current) {
            const script = scriptRef.current;
            MathJax.Hub.Queue(() => {
                const jax = MathJax.Hub.getJaxFor(script);

                if (jax) {
                    jax.Text(formula);
                } else {
                    script.textContent = formula;
                    processMathJaxScript(script);
                }
            });
        } else if (containerRef.current) {
            const script = createMathJaXScript(containerRef.current, inline);
            script.textContent = formula;
            scriptRef.current = script;

            processMathJaxScript(script);
        }
    }, [inline, formula]);

    return unstable_createElement(inline ? 'span' : 'div', {
        ref: containerRef,
        style,
    });
}

/*
 * Create a script.
 */
function createMathJaXScript(
    container: HTMLDivElement | HTMLSpanElement,
    inline: boolean
): HTMLScriptElement {
    const script = document.createElement('script');
    script.type = `math/tex; ${inline ? '' : 'mode=display'}`;
    container.appendChild(script);

    return script;
}

let pendingScripts: HTMLScriptElement[] = [];
let needsProcess = false;

/**
 * Process math in a script node using MathJax.
 */
function processMathJaxScript(script: HTMLScriptElement) {
    pendingScripts.push(script);
    if (!needsProcess) {
        needsProcess = true;
        setTimeout(() => {
            MathJax.Hub.Queue(() => {
                // @ts-ignore elementScripts is not exposed in MathJax's Hub
                const oldElementScripts = MathJax.Hub.elementScripts;
                // @ts-ignore
                MathJax.Hub.elementScripts = () => pendingScripts;

                try {
                    return MathJax.Hub.Process(null, () => {
                        pendingScripts = [];
                        needsProcess = false;
                    });
                } finally {
                    // @ts-ignore
                    MathJax.Hub.elementScripts = oldElementScripts;
                }
            });
        }, 0);
    }
}
